/**
 * MobileBottomNav — Mobile-specific bottom navigation bar.
 *
 * Shows 5 primary nav items at the bottom of the screen on mobile devices.
 * Adapts based on user role to show the most relevant items.
 * Includes a "More" button that opens the full sidebar drawer.
 */

import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Badge } from '@mui/material';
import {
  Home as HomeIcon,
  ConfirmationNumber as TicketIcon,
  Inventory2 as AssetIcon,
  Notifications as NotifIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useDesignSystemStore } from '../store/useDesignSystemStore';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

export const BOTTOM_NAV_HEIGHT = 64;

const NAV_ITEMS = [
  { id: 'home', label: 'Home', path: '/home', icon: HomeIcon },
  { id: 'tickets', label: 'Tickets', path: '/tickets', icon: TicketIcon },
  { id: 'assets', label: 'Assets', path: '/assets', icon: AssetIcon },
  { id: 'notifications', label: 'Alerts', path: '/notifications', icon: NotifIcon, badge: true },
  { id: 'more', label: 'More', path: null, icon: MenuIcon },
];

function NavItem({ item, isActive, onTap, badgeCount }) {
  const Icon = item.icon;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      onClick={() => onTap(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTap(item);
        }
      }}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 0.75,
        gap: 0.25,
        cursor: 'pointer',
        position: 'relative',
        WebkitTapHighlightColor: 'transparent',
        transition: 'all 150ms ease',
        '&:active': {
          transform: 'scale(0.92)',
        },
      }}
    >
      {/* Active indicator dot */}
      {isActive && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: 'primary.main',
          }}
        />
      )}

      {item.badge ? (
        <Badge
          badgeContent={badgeCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              minWidth: 16,
              height: 16,
              top: 2,
              right: -2,
            },
          }}
        >
          <Icon
            sx={{
              fontSize: 22,
              color: isActive ? 'primary.main' : 'text.secondary',
              transition: 'color 150ms ease',
            }}
          />
        </Badge>
      ) : (
        <Icon
          sx={{
            fontSize: 22,
            color: isActive ? 'primary.main' : 'text.secondary',
            transition: 'color 150ms ease',
          }}
        />
      )}

      <Typography
        sx={{
          fontSize: '0.625rem',
          fontWeight: isActive ? 600 : 400,
          color: isActive ? 'primary.main' : 'text.secondary',
          lineHeight: 1,
          letterSpacing: '0.01em',
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
}

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const setMobileDrawerOpen = useDesignSystemStore((s) => s.setMobileDrawerOpen);

  let unreadCount = 0;
  try {
    const notif = useNotifications();
    unreadCount = notif?.unreadCount || 0;
  } catch {
    // NotificationContext might not be available
  }

  const handleTap = useCallback((item) => {
    if (item.id === 'more') {
      setMobileDrawerOpen(true);
    } else if (item.path) {
      navigate(item.path);
    }
  }, [navigate, setMobileDrawerOpen]);

  const isActive = useCallback((item) => {
    if (!item.path) return false;
    if (item.path === '/home') return location.pathname === '/home' || location.pathname === '/';
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  }, [location.pathname]);

  return (
    <Box
      component="nav"
      aria-label="Mobile navigation"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: BOTTOM_NAV_HEIGHT,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1200,
        // Safe area padding for phones with gesture bars
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Frosted glass effect
        backdropFilter: 'blur(12px)',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
      }}
    >
      {NAV_ITEMS.map((item) => (
        <NavItem
          key={item.id}
          item={item}
          isActive={isActive(item)}
          onTap={handleTap}
          badgeCount={item.badge ? unreadCount : 0}
        />
      ))}
    </Box>
  );
}
